import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserContext } from "./UserContext";
import {
  getUnseenAnnouncements,
  getLatestAnnouncement,
  markAnnouncementsSeen,
  getUnseenAlerts,
  getUserAlerts,
  markAlertsSeen,
  deleteAlert,
  deleteAllAlerts,
  type Announcement,
} from "../services/AnnouncementService";

interface AnnouncementContextType {
  unseenCount: number;
  unseenAnnouncements: Announcement[];
  latestAnnouncement: Announcement | null;
  showPopupAnnouncement: Announcement | null;
  dismissPopup: () => void;
  markAllSeen: () => void;
  isLoading: boolean;
  // Alerts
  unseenAlertCount: number;
  unseenAlerts: Announcement[];
  allAlerts: Announcement[];
  markAllAlertsSeen: () => void;
  dismissAlert: (createdAt: number) => void;
  clearAllAlerts: () => void;
  isLoadingAlerts: boolean;
}

const AnnouncementContext = createContext<AnnouncementContextType>({
  unseenCount: 0,
  unseenAnnouncements: [],
  latestAnnouncement: null,
  showPopupAnnouncement: null,
  dismissPopup: () => {},
  markAllSeen: () => {},
  isLoading: false,
  // Alerts
  unseenAlertCount: 0,
  unseenAlerts: [],
  allAlerts: [],
  markAllAlertsSeen: () => {},
  dismissAlert: () => {},
  clearAllAlerts: () => {},
  isLoadingAlerts: false,
});

export function useAnnouncementContext() {
  return useContext(AnnouncementContext);
}

export function AnnouncementProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const queryClient = useQueryClient();
  const [popupDismissed, setPopupDismissed] = useState(false);

  // Only fetch announcements for users with retailers
  const hasRetailers = !!(user?.retailers && user.retailers.length > 0);

  const { data: unseenData, isLoading: unseenLoading } = useQuery({
    queryKey: ["unseen-announcements"],
    queryFn: getUnseenAnnouncements,
    enabled: !!user && hasRetailers,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: latestAnnouncement, isLoading: latestLoading } = useQuery({
    queryKey: ["latest-announcement"],
    queryFn: getLatestAnnouncement,
    enabled: !!user && hasRetailers,
    staleTime: 60 * 1000,
  });

  // Alerts queries
  const { data: unseenAlertsData, isLoading: unseenAlertsLoading } = useQuery({
    queryKey: ["unseen-alerts"],
    queryFn: getUnseenAlerts,
    enabled: !!user && hasRetailers,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: allAlertsData, isLoading: allAlertsLoading } = useQuery({
    queryKey: ["user-alerts"],
    queryFn: () => getUserAlerts(50),
    enabled: !!user && hasRetailers,
    staleTime: 60 * 1000,
  });

  const markSeenMutation = useMutation({
    mutationFn: markAnnouncementsSeen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unseen-announcements"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  const markAlertsSeenMutation = useMutation({
    mutationFn: markAlertsSeen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unseen-alerts"] });
      queryClient.invalidateQueries({ queryKey: ["user-alerts"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  // Determine if we should show a popup announcement
  const showPopupAnnouncement = (() => {
    if (popupDismissed || !latestAnnouncement || !latestAnnouncement.popUp) return null;
    const lastSeen = user?.lastSeenAnnouncementAt || 0;
    if (latestAnnouncement.createdAt <= lastSeen) return null;
    return latestAnnouncement;
  })();

  const dismissPopup = useCallback(() => {
    setPopupDismissed(true);
    markSeenMutation.mutate();
  }, [markSeenMutation]);

  const markAllSeen = useCallback(() => {
    markSeenMutation.mutate();
  }, [markSeenMutation]);

  const markAllAlertsSeen = useCallback(() => {
    markAlertsSeenMutation.mutate();
  }, [markAlertsSeenMutation]);

  const deleteAlertMutation = useMutation({
    mutationFn: deleteAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unseen-alerts"] });
      queryClient.invalidateQueries({ queryKey: ["user-alerts"] });
    },
  });

  const dismissAlert = useCallback((createdAt: number) => {
    deleteAlertMutation.mutate(createdAt);
  }, [deleteAlertMutation]);

  const clearAllAlertsMutation = useMutation({
    mutationFn: deleteAllAlerts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unseen-alerts"] });
      queryClient.invalidateQueries({ queryKey: ["user-alerts"] });
    },
  });

  const clearAllAlerts = useCallback(() => {
    clearAllAlertsMutation.mutate();
  }, [clearAllAlertsMutation]);

  useEffect(() => {
    if (latestAnnouncement) setPopupDismissed(false);
  }, [latestAnnouncement?.createdAt]);

  return (
    <AnnouncementContext.Provider
      value={{
        unseenCount: unseenData?.count || 0,
        unseenAnnouncements: unseenData?.announcements || [],
        latestAnnouncement: latestAnnouncement || null,
        showPopupAnnouncement,
        dismissPopup,
        markAllSeen,
        isLoading: unseenLoading || latestLoading,
        // Alerts
        unseenAlertCount: unseenAlertsData?.count || 0,
        unseenAlerts: unseenAlertsData?.alerts || [],
        allAlerts: allAlertsData?.announcements || [],
        markAllAlertsSeen,
        dismissAlert,
        clearAllAlerts,
        isLoadingAlerts: unseenAlertsLoading || allAlertsLoading,
      }}
    >
      {children}
    </AnnouncementContext.Provider>
  );
}
